'use client'

import { signOut } from '@/app/auth/actions'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState } from 'react'
import { StockSearch } from '../stock/components/StockSearch'
import styles from './header.module.css'

export function Header() {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (isDrawerOpen) setIsDrawerOpen(false)
    }

    // Only add listener if drawer is open
    if (isDrawerOpen) {
      document.addEventListener('click', handleClickOutside)
    }

    return () => {
      document.removeEventListener('click', handleClickOutside)
    }
  }, [isDrawerOpen])

  // Define navigation links with icons
  const links = [
    {
      href: '/', title: 'Home', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
          <polyline points="9 22 9 12 15 12 15 22"></polyline>
        </svg>
      )
    },
    {
      href: '/stock', title: 'Stocks', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 7 13.5 15.5 8.5 10.5 2 17"></polyline>
          <polyline points="16 7 22 7 22 13"></polyline>
        </svg>
      )
    },
    {
      href: '/stock-screener', title: 'Ai Screener', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" >
          <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z" />
          <path d="M9 13a4.5 4.5 0 0 0 3-4" />
          <path d="M6.003 5.125A3 3 0 0 0 6.401 6.5" />
          <path d="M3.477 10.896a4 4 0 0 1 .585-.396" />
          <path d="M6 18a4 4 0 0 1-1.967-.516" />
          <path d="M12 13h4" />
          <path d="M12 18h6a2 2 0 0 1 2 2v1" />
          <path d="M12 8h8" />
          <path d="M16 8V5a2 2 0 0 1 2-2" />
          <circle cx="16" cy="13" r=".5" />
          <circle cx="18" cy="3" r=".5" />
          <circle cx="20" cy="21" r=".5" />
          <circle cx="20" cy="8" r=".5" />
        </svg>
      )
    },
    {
      href: '/profile', title: 'Profile', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
          <circle cx="12" cy="7" r="4"></circle>
        </svg>
      )
    },
    {
      href: '/contact', title: 'Contact Us', icon: (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
        </svg>
      )
    },
  ]

  return (
    <header className={styles.header}>
      <div className={styles.headerLeft}>
        <Link href="/" className={styles.logoLink} // aria-label="SharesGuru Home"
          prefetch={false}
        >
          <Image
            alt="sharesguru"
            height="22"
            width="137"
            src="/logo.svg"
            priority={true}
            loading={'eager'}
            className={styles.desktopLogo}
          />
          <Image
            alt="sharesguru"
            height="28"
            width="28"
            src="/mlogo.svg"
            priority={true}
            loading={'eager'}
            className={styles.mobileLogo}
          />
        </Link>

        <nav className={styles.mainNav}>
          <ul className={styles.navList}>
            {links.slice(1, 3).map((link) => (
              <li key={link.href}>
                <Link href={link.href} className={styles.navLink} prefetch={false}>
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>

      <div className={styles.navContainer}>
        <StockSearch />

        <div className={styles.profileDropdown}>
          <div className={styles.profileButton} tabIndex={0}>
            <svg className={styles.userIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
              <circle cx="12" cy="7" r="4"></circle>
            </svg>
            <span className={styles.srOnly}>Account menu</span>

            <div className={styles.dropdownMenu}>
              <Link href="/profile" className={styles.dropdownItem} prefetch={false}>
                <svg className={styles.dropdownIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                View Profile
              </Link>
              {/* <Link href="/pricing" className={styles.dropdownItem} prefetch={false}>
                <svg className={styles.dropdownIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="1" x2="12" y2="23"></line>
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"></path>
                </svg>
                Pricing
              </Link> */}
              <Link href="/contact" className={styles.dropdownItem} prefetch={false}>
                <svg className={styles.dropdownIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
                </svg>
                Contact Us
              </Link>
              <div className={styles.dropdownDivider}></div>
              <form action={signOut}>
                <button type="submit" className={styles.dropdownItem} style={{ width: '100%' }}>
                  <svg className={styles.dropdownIcon} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                    <polyline points="16 17 21 12 16 7"></polyline>
                    <line x1="21" y1="12" x2="9" y2="12"></line>
                  </svg>
                  Sign Out
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <button
        className={styles.menuButton}
        onClick={(e) => {
          e.stopPropagation();
          setIsDrawerOpen(!isDrawerOpen);
        }}
        // aria-label="Menu"
        aria-expanded={isDrawerOpen}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>

      {/* Mobile drawer */}
      <div
        className={`${styles.drawer} ${isDrawerOpen ? styles.drawerOpen : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.drawerHeader}>
          <Link href="/" className={styles.drawerLogo} onClick={() => setIsDrawerOpen(false)} prefetch={false}>
            <Image
              alt="sharesguru"
              height="22"
              width="137"
              src="/logo.svg"
              priority={true}
            />
          </Link>
          <button
            className={styles.closeDrawer}
            onClick={() => setIsDrawerOpen(false)}
            // aria-label="Close menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </button>
        </div>

        <nav className={styles.drawerNav}>
          <ul className={styles.drawerNavList}>
            {links.map((link) => (
              <li key={link.href}>
                <Link
                  href={link.href}
                  className={styles.drawerNavLink}
                  onClick={() => setIsDrawerOpen(false)}
                  prefetch={false}
                >
                  <span className={styles.drawerIcon}>{link.icon}</span>
                  {link.title}
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className={styles.drawerFooter}>
          <form action={signOut}>
            <button type="submit" className={styles.logoutButton}>
              <svg className={styles.drawerIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
              Sign Out
            </button>
          </form>
        </div>
      </div>

      {/* Backdrop when drawer is open */}
      {isDrawerOpen && <div className={styles.backdrop} />}
    </header>
  )
}